"use client";

import { X } from "lucide-react";
import type { AuthMode } from "../hooks/useAuthForm";
import { Divider, Notice } from "../shared";
import { AuthOAuthButtons } from "./AuthOAuthButtons";
import { AuthEmailForm } from "./AuthEmailForm";

type AuthModalContentProps = {
  mode: AuthMode;
  email: string;
  password: string;
  confirmPassword: string;
  error: string | null;
  message: string | null;
  loading: boolean;
  onEmailChange: (val: string) => void;
  onPasswordChange: (val: string) => void;
  onConfirmPasswordChange: (val: string) => void;
  onEmailSubmit: (e: React.FormEvent) => void;
  onGoogleAuth: () => void;
  onSwitchMode: () => void;
  onClose: () => void;
};

export function AuthModalContent({
  mode,
  email,
  password,
  confirmPassword,
  error,
  message,
  loading,
  onEmailChange,
  onPasswordChange,
  onConfirmPasswordChange,
  onEmailSubmit,
  onGoogleAuth,
  onSwitchMode,
  onClose,
}: AuthModalContentProps) {
  return (
    <>
      {/* Close */}
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 p-1.5 rounded-lg text-[#6b7785] hover:text-[#c7d5e0] hover:bg-[#1e3248]/50 transition-colors"
        aria-label="Close"
      >
        <X className="w-5 h-5" />
      </button>

      {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-white">
          {mode === "sign-in" ? "Welcome back" : "Create account"}
        </h2>
        <p className="text-sm text-[#6b7785] mt-1">
          {mode === "sign-in"
            ? "Sign in to your account"
            : "Get started with a new account"}
        </p>
      </div>

      {/* OAuth */}
      <AuthOAuthButtons onGoogle={onGoogleAuth} />

      <Divider />

      {/* Notices */}
      <Notice kind="error" message={error} />
      <Notice kind="success" message={message} />

      {/* Email form */}
      <AuthEmailForm
        mode={mode}
        email={email}
        password={password}
        confirmPassword={confirmPassword}
        loading={loading}
        onEmailChange={onEmailChange}
        onPasswordChange={onPasswordChange}
        onConfirmPasswordChange={onConfirmPasswordChange}
        onSubmit={onEmailSubmit}
      />

      {/* Switch mode */}
      <ModeSwitch mode={mode} onSwitch={onSwitchMode} />

      {/* /terms says signing up means accepting it — so the sign-up surface
          has to say so too. Plain <a>: the modal sits in the root layout. */}
      <p className="mt-4 text-center text-[11px] leading-relaxed text-[#6b7785]">
        By continuing you agree to the{" "}
        <a href="/terms" className="underline underline-offset-2 hover:text-[#c7d5e0]">
          Terms of Service
        </a>{" "}
        and{" "}
        <a href="/privacy" className="underline underline-offset-2 hover:text-[#c7d5e0]">
          Privacy Policy
        </a>
        .
      </p>
    </>
  );
}

function ModeSwitch({
  mode,
  onSwitch,
}: {
  mode: AuthMode;
  onSwitch: () => void;
}) {
  const isSignIn = mode === "sign-in";

  return (
    <p className="mt-5 text-center text-sm text-[#6b7785]">
      {isSignIn ? "Don\u2019t have an account?" : "Already have an account?"}{" "}
      <button
        type="button"
        onClick={onSwitch}
        className="text-[#4c9cd5] hover:text-[#66c0f4] font-medium transition-colors"
      >
        {isSignIn ? "Sign up" : "Sign in"}
      </button>
    </p>
  );
}
